import { type ReactNode, useMemo } from 'react';
import { Alert, Box, type AlertColor } from '@mui/material';
import {
  Error as ErrorIcon,
  Info as InfoIcon,
  ThumbUp as ThumbUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

export interface InfoBoxProps {
  content: ReactNode;
  type?: 'info' | 'warning' | 'error' | 'success' | 'danger';
  action?: ReactNode;
}

export function InfoBox({ content, type = 'info', action }: InfoBoxProps) {
  const { severity, icon } = useMemo(() => {
    switch (type) {
      case 'info':
        return {
          severity: 'info' as AlertColor,
          icon: <InfoIcon style={{ fontSize: '1.2rem' }} />,
        };
      case 'warning':
        return {
          severity: 'warning' as AlertColor,
          icon: <WarningIcon style={{ fontSize: '1.2rem' }} />,
        };
      case 'error':
      case 'danger': // Legacy support - maps to error
        return {
          severity: 'error' as AlertColor,
          icon: <ErrorIcon style={{ fontSize: '1.2rem' }} />,
        };
      case 'success':
        return {
          severity: 'success' as AlertColor,
          icon: <ThumbUpIcon style={{ fontSize: '1.2rem' }} />,
        };
      default:
        return {
          severity: 'info' as AlertColor,
          icon: <InfoIcon style={{ fontSize: '1.2rem' }} />,
        };
    }
  }, [type]);

  return (
    <Alert
      action={action}
      severity={severity}
      icon={icon}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        position: 'relative',
        '& .MuiAlert-icon': {
          marginRight: 1.5,
          marginTop: 0.25,
        },
      }}
    >
      <Box sx={{ flexGrow: 1, pl: 3 }}>{content}</Box>
    </Alert>
  );
}
